import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Youtube, ArrowUpRight, Play } from 'lucide-react';
import AnimatedText from './AnimatedText';

const YT_VIDEOS = [
  { id: 'ZQn-gAyjV2M', label: 'Latest' },
  { id: '8uojOJLqzZE', label: 'Featured' },
];

const IG_URL = 'https://www.instagram.com/forrealrutviii';
const YT_CHANNEL_URL = 'https://youtube.com/@forrealrutvii';

const reelTiles = [
  { image: 'https://images.unsplash.com/photo-1611605698335-8b1569810432?w=600&auto=format&fit=crop&q=70' },
  { image: 'https://images.unsplash.com/photo-1533062618053-d51e617307ec?w=600&auto=format&fit=crop&q=70' },
  { image: 'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?w=600&auto=format&fit=crop&q=70' },
];

const YouTubeEmbed = ({ id, label, delay = 0 }) => {
  const [loaded, setLoaded] = useState(false);
  const [play, setPlay] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay }}
      whileHover={{ y: -4 }}
      data-testid={`yt-embed-${id}`}
      className="relative aspect-video rounded-[1.5rem] overflow-hidden bg-[#1C1917] shadow-xl group"
    >
      {!play && (
        <button
          onClick={() => setPlay(true)}
          data-cursor-label="Play"
          className="absolute inset-0 z-10 flex flex-col items-center justify-center"
          aria-label={`Play ${label} video`}
        >
          {/* Thumbnail preview */}
          <img
            src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => (e.currentTarget.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-transparent to-[#1C1917]/30" />
          <motion.div
            whileHover={{ scale: 1.12 }}
            className="relative w-20 h-20 rounded-full bg-[#AA94FF] flex items-center justify-center shadow-2xl group-hover:bg-[#F4F4F3] transition-colors"
          >
            <Play className="w-8 h-8 text-[#1C1917] ml-1 fill-current" />
          </motion.div>
          <div className="absolute top-4 left-4 bg-[#F4F4F3]/95 text-[#1C1917] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
            {label}
          </div>
        </button>
      )}
      {play && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={`YouTube · ${label}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity`}
        />
      )}
    </motion.div>
  );
};

const SocialContent = () => {
  return (
    <section
      id="social"
      data-testid="social-content-section"
      className="py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <p className="lg:col-span-4 text-xs font-bold uppercase tracking-[0.25em] text-[#AA94FF]">
            The creator behind HypeLab
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={50}
              stagger={0.05}
            >
              I don't just make content.
            </AnimatedText>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="block text-4xl sm:text-5xl lg:text-6xl italic font-light tracking-[-0.02em] leading-[1] text-[#AA94FF] mt-2"
              style={{ fontFamily: "'Cabinet Grotesk', serif" }}
            >
              I live in it.
            </motion.span>
          </div>
        </div>

        {/* Layout: YouTube (top row 2 videos) + Instagram card (bottom row) */}
        <div className="space-y-6">
          {/* YouTube row — two live embeds */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {YT_VIDEOS.map((v, i) => (
              <YouTubeEmbed key={v.id} id={v.id} label={v.label} delay={i * 0.1} />
            ))}
          </div>

          {/* Meta row: YouTube channel CTA + Instagram card */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {/* YouTube channel link */}
            <motion.a
              href={YT_CHANNEL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="youtube-channel-link"
              data-cursor-label="Watch"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -4 }}
              className="md:col-span-2 group relative overflow-hidden rounded-[1.5rem] bg-[#1C1917] text-[#F4F4F3] p-8 flex flex-col justify-between min-h-[220px]"
            >
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#AA94FF]/30 blur-3xl" />
              <div className="relative flex items-center gap-3">
                <Youtube className="w-6 h-6 text-[#AA94FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em]">
                  YouTube · @forrealrutvii
                </span>
              </div>
              <div className="relative">
                <p
                  className="text-2xl sm:text-3xl font-black leading-[1.05] mb-3"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  Full channel — vlogs, breakdowns, the good stuff.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#AA94FF]">
                  Subscribe <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </motion.a>

            {/* Instagram card */}
            <motion.a
              href={IG_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-card"
              data-cursor-label="Follow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -4 }}
              className="md:col-span-3 group rounded-[1.5rem] bg-white border border-[#1C1917]/10 p-8 flex flex-col justify-between hover:border-[#AA94FF] transition-colors"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Instagram className="w-6 h-6 text-[#AA94FF]" />
                  <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1C1917]">
                    Instagram · @forrealrutviii
                  </span>
                </div>
                <p
                  className="text-xl sm:text-2xl font-black leading-[1.1] text-[#1C1917]"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  Daily reels, stories, UGC shoots.
                </p>
              </div>
              <div className="mt-6 flex items-end justify-between gap-6">
                <div className="grid grid-cols-3 gap-2 flex-1 max-w-[240px]">
                  {reelTiles.map((r, i) => (
                    <div key={i} className="relative aspect-[9/16] rounded-lg overflow-hidden bg-[#E7E5E4]">
                      <img
                        src={r.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/40 to-transparent" />
                      <Play className="absolute bottom-1 left-1 w-3 h-3 text-[#F4F4F3] fill-current" />
                    </div>
                  ))}
                </div>
                <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#AA94FF] shrink-0">
                  Follow <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialContent;
